<template>
    <el-dialog
        title="Test Call"
        :visible.sync="internalVisible"
        width="500px"
        append-to-body
        :close-on-click-modal="false"
        @close="handleClose"
    >
        <div class="test-call-container">
            <!-- Status indicator -->
            <div class="text-center mb-3">
                <div class="call-status" :class="statusClass">
                    <i :class="statusIcon"></i>
                    <span class="ml-2">{{ statusText }}</span>
                </div>
                <div v-if="isActive" class="remaining-time mt-1">
                    <span :class="{ 'text-danger': remainingSeconds <= 10 }">
                        {{ formattedRemainingTime }} remaining
                    </span>
                </div>
            </div>

            <!-- Microphone permission warning -->
            <el-alert
                v-if="microphoneError"
                type="error"
                :title="microphoneError"
                show-icon
                :closable="false"
                class="mb-3"
            />

            <!-- Transcript area -->
            <div
                ref="transcriptContainer"
                class="transcript-container"
            >
                <div v-if="transcript.length === 0 && isActive" class="text-muted text-center py-3">
                    Waiting for conversation...
                </div>
                <div
                    v-for="(entry, index) in transcript"
                    :key="index"
                    class="transcript-entry"
                    :class="{ 'agent-entry': entry.role === ROLE_AGENT, 'user-entry': entry.role === ROLE_USER }"
                >
                    <strong>{{ getRoleLabel(entry.role) }}:</strong>
                    {{ entry.content }}
                </div>
            </div>
        </div>

        <span slot="footer" class="dialog-footer">
            <el-button
                v-if="isIdle || isEnded"
                type="primary"
                :loading="isConnecting"
                @click="startCall"
            >
                <i class="fa fa-phone mr-1"></i>
                Start Call
            </el-button>
            <el-button
                v-if="isActive || isConnecting"
                type="danger"
                @click="endCall"
            >
                <i class="fa fa-phone-slash mr-1"></i>
                End Call
            </el-button>
            <el-button @click="internalVisible = false">
                Close
            </el-button>
        </span>
    </el-dialog>
</template>

<script>
import { RetellWebClient } from 'retell-client-js-sdk'

const ROLE_AGENT = 'agent'
const ROLE_USER = 'user'
const CALL_STATUS_IDLE = 'idle'
const CALL_STATUS_CONNECTING = 'connecting'
const CALL_STATUS_ACTIVE = 'active'
const CALL_STATUS_ENDED = 'ended'
const MAX_CALL_DURATION_SECONDS = 300

export default {
    name: 'TestCallDialog',

    props: {
        visible: {
            type: Boolean,
            required: true,
        },
        botId: {
            type: [Number, String],
            required: true,
        },
    },

    data() {
        return {
            ROLE_AGENT,
            ROLE_USER,
            retellWebClient: null,
            callStatus: CALL_STATUS_IDLE,
            transcript: [],
            microphoneError: null,
            agentTalking: false,
            remainingSeconds: MAX_CALL_DURATION_SECONDS,
            countdownTimer: null,
            callAttemptId: 0,
        }
    },

    computed: {
        internalVisible: {
            get() {
                return this.visible
            },
            set(val) {
                this.$emit('update:visible', val)
            },
        },
        isIdle() {
            return this.callStatus === CALL_STATUS_IDLE
        },
        isConnecting() {
            return this.callStatus === CALL_STATUS_CONNECTING
        },
        isActive() {
            return this.callStatus === CALL_STATUS_ACTIVE
        },
        isEnded() {
            return this.callStatus === CALL_STATUS_ENDED
        },
        statusClass() {
            return {
                'status-idle': this.isIdle,
                'status-connecting': this.isConnecting,
                'status-active': this.isActive,
                'status-ended': this.isEnded,
            }
        },
        statusIcon() {
            if (this.isConnecting) {
                return 'fa fa-spinner fa-spin'
            }
            if (this.isActive) {
                return this.agentTalking ? 'fa fa-volume-up' : 'fa fa-phone'
            }
            if (this.isEnded) {
                return 'fa fa-phone-slash'
            }
            return 'fa fa-phone'
        },
        statusText() {
            if (this.isConnecting) {
                return 'Connecting...'
            }
            if (this.isActive) {
                return this.agentTalking ? 'AloAi is speaking...' : 'Call in progress'
            }
            if (this.isEnded) {
                return 'Call ended'
            }
            return 'Ready to call'
        },
        formattedRemainingTime() {
            const minutes = Math.floor(this.remainingSeconds / 60)
            const seconds = this.remainingSeconds % 60
            return `${minutes}:${String(seconds).padStart(2, '0')}`
        },
    },

    watch: {
        visible(val) {
            if (val) {
                this.resetState()
            }
        },
    },

    mounted() {
        this.onUnhandledRejection = (event) => {
            if (event.reason?.name === 'PublishTrackError') {
                event.preventDefault()
                console.warn('Test call: WebRTC track publish failed -', event.reason.message)
            }
        }
        window.addEventListener('unhandledrejection', this.onUnhandledRejection)
    },

    beforeDestroy() {
        this.cleanup()
        window.removeEventListener('unhandledrejection', this.onUnhandledRejection)
    },

    methods: {
        getRoleLabel(role) {
            return role === ROLE_AGENT ? 'AloAi' : 'You'
        },

        resetState() {
            this.callStatus = CALL_STATUS_IDLE
            this.transcript = []
            this.microphoneError = null
            this.agentTalking = false
            this.remainingSeconds = MAX_CALL_DURATION_SECONDS
            this.stopCountdown()
        },

        startCountdown() {
            this.remainingSeconds = MAX_CALL_DURATION_SECONDS
            this.countdownTimer = setInterval(() => {
                this.remainingSeconds--
                if (this.remainingSeconds <= 0) {
                    this.endCall()
                }
            }, 1000)
        },

        stopCountdown() {
            if (this.countdownTimer) {
                clearInterval(this.countdownTimer)
                this.countdownTimer = null
            }
        },

        async startCall() {
            if (this.isConnecting) {
                return
            }

            this.cleanup()

            const attemptId = ++this.callAttemptId
            this.callStatus = CALL_STATUS_CONNECTING
            this.microphoneError = null
            this.transcript = []

            try {
                const { data } = await axios.post(`/api/v1/aloai/bot/${this.botId}/test-call`)

                if (attemptId !== this.callAttemptId || !this.visible) {
                    return
                }

                const client = new RetellWebClient()
                this.retellWebClient = client

                client.on('call_started', () => {
                    if (attemptId !== this.callAttemptId) return
                    this.callStatus = CALL_STATUS_ACTIVE
                    this.startCountdown()
                })

                client.on('call_ended', () => {
                    if (attemptId !== this.callAttemptId) return
                    this.callStatus = CALL_STATUS_ENDED
                    this.stopCountdown()
                })

                client.on('agent_start_talking', () => {
                    if (attemptId !== this.callAttemptId) return
                    this.agentTalking = true
                })

                client.on('agent_stop_talking', () => {
                    if (attemptId !== this.callAttemptId) return
                    this.agentTalking = false
                })

                client.on('update', (update) => {
                    if (attemptId !== this.callAttemptId) return
                    if (update.transcript) {
                        this.transcript = update.transcript
                        this.$nextTick(() => {
                            this.scrollTranscript()
                        })
                    }
                })

                client.on('error', (error) => {
                    if (attemptId !== this.callAttemptId) return
                    console.error('Test call error:', error)
                    this.retellWebClient = null
                    this.callStatus = CALL_STATUS_ENDED
                    this.stopCountdown()
                    this.microphoneError = 'Call error occurred. Please try again.'
                })

                await client.startCall({
                    accessToken: data.access_token,
                })
            } catch (error) {
                if (attemptId !== this.callAttemptId) return

                this.callStatus = CALL_STATUS_IDLE

                if (error.name === 'NotAllowedError' || error.message?.includes('microphone')) {
                    this.microphoneError = 'Microphone access is required for test calls. Please allow microphone access and try again.'
                } else {
                    this.microphoneError = error.response?.data?.message || 'Failed to start test call. Please try again.'
                }
            }
        },

        endCall() {
            this.cleanup()
            this.callStatus = CALL_STATUS_ENDED
        },

        handleClose() {
            this.cleanup()
        },

        cleanup() {
            this.callAttemptId++
            this.stopCountdown()
            if (this.retellWebClient) {
                this.retellWebClient.stopCall()
                this.retellWebClient = null
            }
        },

        scrollTranscript() {
            const container = this.$refs.transcriptContainer
            if (container) {
                container.scrollTop = container.scrollHeight
            }
        },
    },
}
</script>

<style scoped>
.test-call-container {
    min-height: 200px;
}

.call-status {
    font-size: 1.1rem;
    padding: 8px 16px;
    border-radius: 20px;
    display: inline-block;
}

.remaining-time {
    font-size: 0.85rem;
    color: #909399;
}

.status-idle {
    color: #909399;
    background: #f4f4f5;
}

.status-connecting {
    color: #e6a23c;
    background: #fdf6ec;
}

.status-active {
    color: #67c23a;
    background: #f0f9eb;
}

.status-ended {
    color: #909399;
    background: #f4f4f5;
}

.transcript-container {
    max-height: 300px;
    overflow-y: auto;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    padding: 12px;
    background: #fafafa;
}

.transcript-entry {
    margin-bottom: 8px;
    padding: 6px 10px;
    border-radius: 6px;
    font-size: 0.9rem;
    line-height: 1.4;
    overflow-wrap: break-word;
    word-break: normal;
}

.agent-entry {
    background: #ecf5ff;
    color: #303133;
}

.user-entry {
    background: #f0f9eb;
    color: #303133;
}
</style>
